import { GraduationCap, Upload, UserRound } from 'lucide-react'
import { useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useUniversities } from '../api/referenceApi'
import { FormField } from '../components/FormField'
import { CINSIYET_VALUES, EGITIM_DURUMU_VALUES, MEDENI_VALUES, type PersonelFormValues } from '../schemas/personelFormSchema'
import { FormSection } from './FormBits'
import { FULL_ROW } from './formTabs'
import { useFieldError } from './useFieldError'

export function KisiselFormTab() {
  const { t } = useTranslation()
  const { register, control, setValue, watch } = useFormContext<PersonelFormValues>()
  const err = useFieldError()
  const unisQ = useUniversities()
  const photoRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const fotoAdi = watch('foto_adi')
  const opts = (group: string, values: readonly string[]) =>
    values.map((v) => ({ value: v, label: t(`hr.personel.form.options.${group}.${v}`) }))

  return (
    <div className="space-y-4">
      <FormSection title={t('hr.personel.genel.kisisel')} icon={<UserRound />} description={t('hr.personel.form.hints.kisisel')}>
        <div className={FULL_ROW}>
          <FormField label={t('hr.personel.form.photo')} error={err('foto_adi')}>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex size-16 items-center justify-center overflow-hidden rounded-xl border border-border/70 bg-muted/40 sm:size-20 sm:rounded-2xl">
                {preview ? <img src={preview} alt="" className="size-full object-cover" /> : <UserRound className="size-6 text-muted-foreground sm:size-8" />}
              </div>
              <div className="space-y-2">
                <input
                  ref={photoRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    setValue('foto_adi', file.name, { shouldValidate: true, shouldDirty: true })
                    setValue('foto_dosya', file, { shouldDirty: true })
                    setPreview((old) => {
                      if (old) URL.revokeObjectURL(old)
                      return URL.createObjectURL(file)
                    })
                  }}
                />
                <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => photoRef.current?.click()}>
                  <Upload className="size-4" />
                  {t('hr.personel.form.uploadPhoto')}
                </Button>
                {fotoAdi ? <p className="text-xs text-muted-foreground">{fotoAdi}</p> : null}
              </div>
            </div>
          </FormField>
        </div>
        <FormField label={t('hr.personel.fields.ad')} htmlFor="f-ad" required error={err('ad')}>
          <Input id="f-ad" className="h-10 rounded-xl" {...register('ad')} />
        </FormField>
        <FormField label={t('hr.personel.fields.dogum')} htmlFor="f-dogum" required error={err('dogum')}>
          <Controller
            control={control}
            name="dogum"
            render={({ field }) => (
              <DateField id="f-dogum" className="h-10 rounded-xl" value={field.value} onChange={field.onChange} aria-invalid={!!err('dogum')} aria-label={t('hr.personel.fields.dogum')} />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.fields.tckn')} htmlFor="f-tckn" required error={err('tckn')}>
          <Input id="f-tckn" inputMode="numeric" maxLength={11} className="h-10 rounded-xl" {...register('tckn')} />
        </FormField>
        <FormField label={t('hr.personel.fields.uyruk')} htmlFor="f-uyruk" required error={err('uyruk')}>
          <Input id="f-uyruk" className="h-10 rounded-xl" {...register('uyruk')} />
        </FormField>
        <FormField label={t('hr.personel.fields.cinsiyet')} required error={err('cinsiyet')}>
          <Controller
            control={control}
            name="cinsiyet"
            render={({ field }) => <Select size="lg" value={field.value} onChange={field.onChange} options={opts('cinsiyet', CINSIYET_VALUES)} aria-label={t('hr.personel.fields.cinsiyet')} />}
          />
        </FormField>
        <FormField label={t('hr.personel.fields.medeni')} required error={err('medeni')}>
          <Controller
            control={control}
            name="medeni"
            render={({ field }) => <Select size="lg" value={field.value} onChange={field.onChange} options={opts('medeni', MEDENI_VALUES)} aria-label={t('hr.personel.fields.medeni')} />}
          />
        </FormField>
      </FormSection>

      <FormSection title={t('hr.personel.form.sections.egitim')} icon={<GraduationCap />}>
        <FormField label={t('hr.personel.form.egitimDurumu')} required error={err('egitim_durumu')}>
          <Controller
            control={control}
            name="egitim_durumu"
            render={({ field }) => <Select size="lg" value={field.value} onChange={field.onChange} options={opts('egitim', EGITIM_DURUMU_VALUES)} aria-label={t('hr.personel.form.egitimDurumu')} />}
          />
        </FormField>
        <FormField label={t('hr.personel.form.universite')} error={err('universite_id')}>
          <Controller
            control={control}
            name="universite_id"
            render={({ field }) => (
              <Select
                searchable
                size="lg"
                className="rounded-xl"
                creatable
                value={field.value ?? ''}
                onChange={field.onChange}
                options={(unisQ.data ?? []).map((name) => ({ value: name, label: name }))}
                loading={unisQ.isLoading}
                placeholder={t('hr.personel.form.selectUniversity')}
                searchPlaceholder={t('hr.personel.form.searchUniversity')}
                aria-label={t('hr.personel.form.universite')}
              />
            )}
          />
        </FormField>
        <FormField label={t('hr.personel.form.mezunBolum')} htmlFor="f-bolum" error={err('mezun_bolum')}>
          <Input id="f-bolum" className="h-10 rounded-xl" {...register('mezun_bolum')} />
        </FormField>
      </FormSection>
    </div>
  )
}
